import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO, { SEOConfigs } from '../components/SEO';
import { 
  TrendingUp, Zap, BarChart3, Shield, Brain, Users, CheckCircle, 
  Star, ArrowRight, Store, Share2, Play, Sparkles, Award, 
  Activity, Target, Lock, Clock, ChevronRight, Globe
} from 'lucide-react';
import { motion } from 'framer-motion';

const LandingPage = () => {
  const features = [
    { 
      icon: <Activity className="h-6 w-6" />, 
      title: 'Doğrulanmış Sinyaller', 
      desc: 'Gerçek işlemlerle ölçülen, canlı performanslı sinyaller. Tahmin değil, şeffaf sonuç.',
      color: '#8B5CF6'
    },
    { 
      icon: <Zap className="h-6 w-6" />, 
      title: 'Kod Yazmadan Bot Oluştur', 
      desc: 'Görsel editörle dakikalar içinde al-sat botunu kur. Yazılım bilmeden otomatik trade.',
      color: '#00E599'
    },
    { 
      icon: <BarChart3 className="h-6 w-6" />, 
      title: 'Canlı Performans & Gerçek Sonuç', 
      desc: 'Backtest değil, gerçek piyasa verisi. Kazanan da kaybeden de açıkça görünür.',
      color: '#2E90FA'
    },
    { 
      icon: <Store className="h-6 w-6" />, 
      title: 'Sinyal & Bot Satışı', 
      desc: 'Stratejini pazaryerinde yayınla, abonelikten düzenli gelir elde et.',
      color: '#F59E0B'
    },
    { 
      icon: <Brain className="h-6 w-6" />, 
      title: 'AI Destekli Strateji Geliştirme', 
      desc: 'Yapay zeka ile optimize et, riskleri azalt, daha tutarlı stratejiler oluştur.',
      color: '#10B981'
    },
    { 
      icon: <Shield className="h-6 w-6" />, 
      title: 'Otomatik Risk Yönetimi', 
      desc: 'Otomatik stop, hedef, pozisyon boyutlandırma. Kontrol sende, disiplin sistemde.',
      color: '#EC4899'
    }
  ];

  const earningModels = [
    {
      icon: <Activity className="h-8 w-8" />,
      tag: 'Takip Et',
      title: 'Profesyonel Sinyaller',
      subtitle: 'Doğrulanmış sinyalleri anında al',
      description: 'Doğrulanmış sinyal sağlayıcıları keşfet, al-sat sinyallerini web ve Telegram üzerinden anında al.',
      benefits: ['Gerçek performans sıralaması', 'Anlık bildirimler', 'Kolay abonelik'],
      cta: 'Sinyalleri Keşfet',
      link: '/subscriber',
      color: '#8B5CF6',
      gradient: 'from-purple-500 to-violet-600'
    },
    {
      icon: <Zap className="h-8 w-8" />,
      tag: 'Oluştur',
      title: 'Otomatik Bot Oluştur',
      subtitle: 'Kod yazmadan al-sat botunu kur',
      description: 'AI destekli görsel editör ile kendi al-sat botunu oluştur, backtest yap ve canlıda çalıştır.',
      benefits: ['No-Code bot oluşturma', 'Backtest & canlı performans', 'AI asistan'],
      cta: 'Botunu Oluştur',
      link: '/trader',
      color: '#00E599',
      gradient: 'from-emerald-500 to-green-600'
    },
    {
      icon: <Store className="h-8 w-8" />,
      tag: 'Yayınla',
      title: 'Sinyal Sağlayıcı Ol',
      subtitle: 'Stratejini yayınla, abonelikten kazan',
      description: 'Sinyallerini veya botlarını pazaryerinde yayınla, abonelerden düzenli gelir elde et.',
      benefits: ['%85 gelir payı', 'Otomatik ödeme', 'Şeffaf performans'],
      cta: 'Sağlayıcı Ol',
      link: '/seller',
      color: '#2E90FA',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Share2 className="h-8 w-8" />,
      tag: 'Kazan',
      title: 'Partner Ol',
      subtitle: 'Davet et, ömür boyu komisyon kazan',
      description: 'Arkadaşlarını davet et, her ödemeden %20 komisyon kazan. Ömür boyu gelir.',
      benefits: ['%20 komisyon', 'Ömür boyu gelir', 'Anlık takip'],
      cta: 'Partner Ol',
      link: '/partner',
      color: '#F59E0B',
      gradient: 'from-yellow-500 to-orange-500'
    }
  ];

  const steps = [
    { num: '1', title: 'Kayıt Ol', desc: 'Ücretsiz hesabını oluştur', icon: <Users className="h-5 w-5" /> },
    { num: '2', title: 'Sinyal Al veya Bot Kur', desc: 'Hazır sinyalleri takip et ya da kendi botunu oluştur', icon: <Zap className="h-5 w-5" /> },
    { num: '3', title: 'Gerçek Performansı Gör', desc: 'Canlı sonuçları, başarı oranını takip et', icon: <BarChart3 className="h-5 w-5" /> },
    { num: '4', title: 'Kazan', desc: 'Sinyallerle trade et veya stratejini pazaryerinde sat', icon: <TrendingUp className="h-5 w-5" /> }
  ];

  const usageScenarios = [
    { 
      persona: 'Profesyonel Sinyaller',
      badge: 'Yeni Başlayanlar İçin',
      badgeColor: '#8B5CF6',
      icon: '📡',
      steps: ['Sinyal sağlayıcıları keşfetti', 'Performansa göre abone oldu', 'Telegram\'dan anlık sinyal almaya başladı'],
      color: '#8B5CF6',
      cta: '📈 Sinyal Almaya Başla',
      link: '/subscriber'
    },
    { 
      persona: 'Otomatik Bot',
      badge: 'Aktif Trader\'lar',
      badgeColor: '#00E599',
      icon: '🤖',
      steps: ['Kod bilmeden RSI + EMA botu oluşturdu', '14 gün boyunca test etti, canlıya hazır hale getirdi', 'Otomatik sinyal takibiyle trade yapıyor'],
      color: '#00E599',
      cta: '🤖 Botunu Şimdi Kur',
      link: '/trader'
    },
    { 
      persona: 'Sinyal Sağlayıcı',
      badge: 'Kazanç Odaklı',
      badgeColor: '#F59E0B',
      icon: '💰',
      steps: ['Kendi analizleriyle sinyal paylaştı', 'Pazaryerinde stratejisini yayınladı', 'Her ay düzenli gelir kazanmaya başladı'],
      color: '#2E90FA',
      cta: '💰 Gelir Elde Et',
      link: '/seller'
    }
  ];

  const trustedBy = ['TradingView', 'CoinGecko', 'Yahoo Finance'];

  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO {...SEOConfigs.home} />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-brand-green/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px]" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green/10 border border-brand-green/20 mb-8"
            >
              <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse" />
              <span className="text-brand-green text-sm font-medium">
                Türkiye'nin #1 Trading Sinyal Platformu
              </span>
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold mb-6 text-white leading-[1.1]">
              <span className="block">Trading Botları</span>
              <span className="block">ve Sinyal Sağlayıcılar</span>
              <span className="text-3xl md:text-5xl lg:text-6xl font-medium mt-4 block">
                <span className="text-brand-green">Tek Platformda</span>
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-400 mb-10 max-w-3xl mx-auto leading-relaxed">
              Uzman sinyalleriyle kazanın, kod yazmadan botunuzu oluşturun ve stratejinizi paylaşarak gelir elde edin.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
              <Link to="/register">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8 py-7 text-lg rounded-xl shadow-lg shadow-brand-green/25">
                  Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/marketplace">
                <Button size="lg" variant="outline" className="border-slate-700 text-white hover:bg-slate-800 px-8 py-7 text-lg rounded-xl">
                  <Play className="mr-2 h-5 w-5" /> Pazaryerini Keşfet
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-12 border-y border-slate-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50">
            <span className="text-slate-500 text-sm">Veri Kaynakları:</span>
            {trustedBy.map((brand, idx) => (
              <span key={idx} className="text-slate-400 font-semibold text-lg">{brand}</span>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Kullanım Yolu */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 px-4 py-2 bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                <Sparkles className="h-4 w-4 mr-2" /> 4 Farklı Kullanım Yolu
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Borsamex ile <span className="text-brand-green">Kazan</span>
              </h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                Sinyal takip et, kendi botunu yap, sat veya referans ver - sana uygun yolu seç
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {earningModels.map((model, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                viewport={{ once: true }}
                className="group"
              >
                <Card className="h-full border-2 border-slate-800 bg-slate-900/50 hover:border-slate-700 hover:bg-slate-800/50 transition-all duration-300">
                  <CardContent className="p-6">
                    {/* Tag */}
                    {model.tag && (
                      <span 
                        className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-3"
                        style={{ backgroundColor: model.color + '20', color: model.color }}
                      >
                        {model.tag}
                      </span>
                    )}
                    
                    {/* Icon */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                      style={{ backgroundColor: model.color + '20', color: model.color }}
                    >
                      {model.icon}
                    </div>

                    {/* Title */}
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-white mb-0.5">{model.title}</h3>
                      <p className="text-xs font-medium" style={{ color: model.color }}>{model.subtitle}</p>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-slate-400 mb-4 line-clamp-2">{model.description}</p>

                    {/* Benefits */}
                    <div className="space-y-2 mb-5">
                      {model.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: model.color }} />
                          <span className="text-sm text-slate-300">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <Link to={model.link}>
                      <Button 
                        className="w-full py-2.5 text-sm font-semibold text-white"
                        style={{ backgroundColor: model.color }}
                      >
                        {model.cta} <ChevronRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 px-3 py-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
              Farkımız
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Borsamex Neden Farklı?</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Telegram gruplarının, klasik botların ve kapalı sistemlerin ötesinde
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-slate-800 bg-slate-900/30 hover:bg-slate-800/50 transition-all">
                  <CardContent className="p-6">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: f.color + '20', color: f.color }}
                    >
                      {f.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{f.title}</h3>
                    <p className="text-slate-400">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-lg md:text-xl text-slate-300 font-medium">
              <span className="text-primary">Borsamex</span>, sinyal alan, bot yapan ve strateji satan herkes için <span className="text-white">tek platform</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">4 Adımda Trading'e Başla</h2>
            <p className="text-lg text-slate-400">Sinyal al, bot kur veya stratejini satarak kazan</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.15 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                {idx < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-brand-green/50 to-transparent" />
                )}
                <div className="w-16 h-16 rounded-2xl bg-brand-green/20 border-2 border-brand-green/40 flex items-center justify-center mx-auto mb-4 text-brand-green relative z-10">
                  {step.icon}
                </div>
                <div className="text-sm font-bold text-brand-green mb-2">ADIM {step.num}</div>
                <h3 className="text-lg font-bold text-white mb-1">{step.title}</h3>
                <p className="text-sm text-slate-500">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-lg text-slate-400">
              <span className="text-primary font-semibold">Borsamex'te</span> herkes kendi yoluyla kazanır.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Kullanım Senaryoları */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
              <Users className="h-3 w-3 mr-1" /> Kullanım Senaryoları
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Borsamex'i <span className="text-primary">Nasıl Kullanmak</span> İstersin?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Tek platform, üç farklı kazanma yolu
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {usageScenarios.map((scenario, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-slate-800 bg-slate-900/30 hover:border-slate-700 transition-all">
                  <CardContent className="p-6 h-full flex flex-col">
                    {/* Badge */}
                    <div className="mb-4">
                      <span 
                        className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                        style={{ backgroundColor: scenario.badgeColor + '20', color: scenario.badgeColor }}
                      >
                        {scenario.badge}
                      </span>
                    </div>
                    
                    {/* Persona Header */}
                    <div className="flex items-center gap-3 mb-5">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: scenario.color + '20' }}
                      >
                        {scenario.icon}
                      </div>
                      <div>
                        <div className="font-bold text-white text-lg">{scenario.persona}</div>
                      </div>
                    </div>
                    
                    {/* Steps */}
                    <div className="space-y-3 mb-5 flex-grow">
                      {scenario.steps.map((step, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div 
                            className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: scenario.color + '30', color: scenario.color }}
                          >
                            {i + 1}
                          </div>
                          <span className="text-sm text-slate-300">{step}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* CTA Button - Always at bottom */}
                    <div className="mt-auto">
                      <Link to={scenario.link}>
                        <Button 
                          variant="outline" 
                          className="w-full border-slate-700 hover:border-slate-600 text-white"
                          style={{ borderColor: scenario.color + '40', color: scenario.color }}
                        >
                          {scenario.cta} <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                      
                      {/* Tag */}
                      <div className="pt-4 mt-4 border-t border-slate-800">
                        <span className="text-xs text-slate-500 flex items-center gap-1.5">
                          <CheckCircle className="h-3 w-3 text-primary" />
                          Gerçek kullanım senaryosu
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-green to-emerald-600" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
            <div className="relative z-10 text-center py-16 md:py-20 px-8">
              <h2 className="text-4xl md:text-6xl font-bold text-brand-dark mb-4">
                Şimdi Sana Uygun Yoldan Başla
              </h2>
              <p className="text-xl text-brand-dark/70 mb-8 max-w-xl mx-auto">
                Ücretsiz hesap oluştur · Dakikalar içinde başla
              </p>
              <Link to="/pricing">
                <Button size="lg" className="bg-brand-dark hover:bg-brand-dark/90 text-white font-bold px-10 py-7 text-lg rounded-xl">
                  Ücretsiz Denemeye Başla <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
